import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import {
  ASSIGN_CHAMPIONSHIP_TEMPLATE_GROUP_MUTATION,
  CHAMPIONSHIP_MEDIA_SETTINGS_QUERY,
  ChampionshipMediaKindGql,
  ChampionshipMediaRuleGql,
  ChampionshipMediaSettings,
  ChampionshipMediaSettingsGql,
  UNASSIGN_CHAMPIONSHIP_TEMPLATE_GROUP_MUTATION,
  UPDATE_TOURNAMENT_TIMEZONE_MUTATION,
} from 'modules/championship/domain/graphql/championship-media';
import { MediaKind, isKnownMediaKind, isVideoMediaKind } from 'modules/manager/domain/media-kind';
import { baseApi } from 'shared/api/base-api';

export const CHAMPIONSHIP_MEDIA_SETTINGS_TAG = 'ChampionshipMediaSettings' as const;

function mapBlockedReason(value: string | null | undefined): ChampionshipMediaSettings['kinds'][number]['blockedReason'] {
  if (value === 'no_group' || value === 'unsupported' || value === 'no_template' || value === 'no_plan_feature') {
    return value;
  }
  return null;
}

function mapSettings(raw: ChampionshipMediaSettingsGql): ChampionshipMediaSettings {
  const rulesByKind = new Map<string, ChampionshipMediaRuleGql>();
  raw.rules.forEach((rule) => rulesByKind.set(rule.kind, rule));

  const kinds = raw.kinds
    // Video review kinds are never generated: the backend keeps them out, and so do we (IMGM-1606).
    .filter(
      (kind): kind is ChampionshipMediaKindGql & { kind: MediaKind } => isKnownMediaKind(kind.kind) && !isVideoMediaKind(kind.kind),
    )
    .map((kind) => {
      const rule = rulesByKind.get(kind.kind);
      return {
        kind: kind.kind,
        title: kind.title,
        templateType: kind.templateType,
        subjectType: kind.subjectType,
        supported: kind.supported,
        enabled: rule?.enabled ?? false,
        templateName: rule?.template?.name ?? null,
        blockedReason: mapBlockedReason(rule?.blockedReason),
      };
    });

  return {
    championshipId: raw.championshipId,
    timezone: raw.timezone,
    templateGroup: raw.templateGroup,
    kinds,
  };
}

/**
 * Назначение и отвязка набора отдают настройки целиком — кладём их в кэш вместо инвалидации тега
 * (пессимистичное обновление, https://redux-toolkit.js.org/rtk-query/usage/optimistic-updates).
 * Инвалидация давала второй запрос за тем же, и до его ответа экран показывал прежний набор (IMGM-1513).
 *
 * Сначала `updateQueryData` с заменой записи целиком: он синхронный, и к возврату `unwrap()` в
 * компоненте кэш уже новый. `upsertQueryData` в RTK 1.9 идёт через thunk и пишет кэш на несколько
 * микрозадач позже — экран успевал перерисоваться со старым набором. Он остаётся запасным путём,
 * когда записи под ключом нет (на ней `updateQueryData` молча ничего не делает).
 */
async function putSettingsIntoCache(
  championshipId: number,
  dispatch: ThunkDispatch<any, unknown, AnyAction>,
  queryFulfilled: Promise<{ data: ChampionshipMediaSettings }>,
): Promise<void> {
  let data: ChampionshipMediaSettings;
  try {
    ({ data } = await queryFulfilled);
  } catch {
    // Ошибку показывает вызывающая сторона; кэш остаётся прежним.
    return;
  }
  const patch = dispatch(championshipMediaApi.util.updateQueryData('championshipMediaSettings', championshipId, () => data));
  if (patch.patches.length === 0) {
    dispatch(championshipMediaApi.util.upsertQueryData('championshipMediaSettings', championshipId, data));
  }
}

export const championshipMediaApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    championshipMediaSettings: build.query<ChampionshipMediaSettings, number>({
      query: (championshipId) => ({
        query: CHAMPIONSHIP_MEDIA_SETTINGS_QUERY,
        variables: { championshipId },
      }),
      transformResponse: (raw: { championshipMediaSettings: ChampionshipMediaSettingsGql }) =>
        mapSettings(raw.championshipMediaSettings),
      providesTags: (_result, _error, championshipId) => [{ type: CHAMPIONSHIP_MEDIA_SETTINGS_TAG, id: championshipId }],
    }),
    assignChampionshipTemplateGroup: build.mutation<ChampionshipMediaSettings, { championshipId: number; templateGroupId: number }>({
      query: ({ championshipId, templateGroupId }) => ({
        query: ASSIGN_CHAMPIONSHIP_TEMPLATE_GROUP_MUTATION,
        variables: { championshipId, templateGroupId },
      }),
      transformResponse: (raw: { assignChampionshipTemplateGroup: ChampionshipMediaSettingsGql }) =>
        mapSettings(raw.assignChampionshipTemplateGroup),
      onQueryStarted: ({ championshipId }, { dispatch, queryFulfilled }) =>
        putSettingsIntoCache(championshipId, dispatch, queryFulfilled),
    }),
    unassignChampionshipTemplateGroup: build.mutation<ChampionshipMediaSettings, number>({
      query: (championshipId) => ({
        query: UNASSIGN_CHAMPIONSHIP_TEMPLATE_GROUP_MUTATION,
        variables: { championshipId },
      }),
      transformResponse: (raw: { unassignChampionshipTemplateGroup: ChampionshipMediaSettingsGql }) =>
        mapSettings(raw.unassignChampionshipTemplateGroup),
      onQueryStarted: (championshipId, { dispatch, queryFulfilled }) => putSettingsIntoCache(championshipId, dispatch, queryFulfilled),
    }),
    updateTournamentTimezone: build.mutation<{ id: number; timezone: string }, { tournamentId: number; timezone: string }>({
      query: ({ tournamentId, timezone }) => ({
        query: UPDATE_TOURNAMENT_TIMEZONE_MUTATION,
        variables: { tournamentId, timezone },
      }),
      transformResponse: (raw: { updateTournamentTimezone: { id: number; timezone: string } }) => raw.updateTournamentTimezone,
    }),
  }),
  overrideExisting: false,
});

export const {
  useChampionshipMediaSettingsQuery,
  useAssignChampionshipTemplateGroupMutation,
  useUnassignChampionshipTemplateGroupMutation,
  useUpdateTournamentTimezoneMutation,
} = championshipMediaApi;
